import { z } from "zod";

/**
 * StudyWard Core library (owner decision OD-1, plan §5.2a, BE10): NCLEX sets StudyWard generates once from openly
 * licensed nursing textbooks, owned by a system account and readable by every student who opts in. Turning it off
 * hides them; reset clears only the student's Core review schedule. Their own sets are never touched.
 */
export const CORE_LIBRARY_OWNER_ID = "c0de0000-0000-4000-8000-000000000001";

export const SET_ORIGINS = ["user", "core"] as const;
export const setOriginSchema = z.enum(SET_ORIGINS);
export type SetOrigin = z.infer<typeof setOriginSchema>;

/** GET /core-library */
export const coreLibraryStatusSchema = z.object({
  /** At least one published Core set exists for the student's exam. */
  available: z.boolean(),
  enabled: z.boolean(),
  reset_at: z.string().nullable(),
  sets: z.number().int().nonnegative(),
  questions: z.number().int().nonnegative(),
  cards: z.number().int().nonnegative(),
});
export type CoreLibraryStatus = z.infer<typeof coreLibraryStatusSchema>;

/** PUT /core-library */
export const coreLibraryUpdateSchema = z.object({ enabled: z.boolean() });

/** POST /core-library/reset */
export const coreLibraryResetResponseSchema = z.object({ cleared_cards: z.number().int().nonnegative(), reset_at: z.string() });

// ---------------------------------------------------------------- ingestion (owner, source-agnostic — plan BE11)
/** Stored format of an ingested Core source: named sections, so citations read "Book title, 12.3" and not "¶4". */
export const CORE_SECTIONS_MIME = "application/vnd.studyward.core-sections+json";
export const coreSectionSchema = z.object({
  /** Citation anchor shown to students, e.g. "Pharmacology for Nurses, 12.3 Anticoagulants". */
  anchor: z.string().trim().min(1).max(80),
  text: z.string().trim().min(50).max(20_000),
});
export const coreSectionsSchema = z.array(coreSectionSchema).min(1).max(300);

/** Where the material comes from and under which licence — required for every Core set, shown to students. */
export const coreAttributionSchema = z.object({
  source_title: z.string().trim().min(1).max(200),
  authors: z.string().trim().max(300).default(""),
  /** The licence that allows StudyWard's commercial use, e.g. "Commercial licence from Rice University, 2026". */
  licence: z.string().trim().min(3).max(200),
  licence_url: z.string().trim().url().max(500).nullable().default(null),
  source_url: z.string().trim().url().max(500).nullable().default(null),
});
export type CoreAttribution = z.infer<typeof coreAttributionSchema>;

/** POST /admin/core-library/sets — one Core set from one licensed source, generated like any other set. */
export const coreIngestRequestSchema = z
  .object({
    idempotency_key: z.string().min(8).max(80),
    title: z.string().trim().min(1).max(120),
    exam: z.enum(["nclex_rn", "nclex_pn"]),
    attribution: coreAttributionSchema,
    sections: coreSectionsSchema,
  })
  .refine((b) => b.sections.reduce((n, s) => n + s.text.length, 0) <= 500_000, { message: "Keep one Core set under 500,000 characters — split the source.", path: ["sections"] });
export type CoreIngestRequest = z.infer<typeof coreIngestRequestSchema>;

/** Admin list of Core sets with where each one stands on its way to students. */
export const adminCoreSetSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  exam: z.string(),
  status: z.string(),
  attribution: coreAttributionSchema,
  questions: z.number().int(),
  cards: z.number().int(),
  reviews: z.object({ pending: z.number().int(), approved: z.number().int(), quarantined: z.number().int(), unqueued: z.number().int() }),
  released_at: z.string().nullable(),
  /** Ready, every published item approved, not yet released. */
  can_release: z.boolean(),
  created_at: z.string(),
});
export type AdminCoreSet = z.infer<typeof adminCoreSetSchema>;
export const adminCoreSetListSchema = z.object({ items: z.array(adminCoreSetSchema) });
